import {
    FetchState,
    ContactInfoState,
    ContactInformation,
    FetchStateKey,
    LobAddressValidationResult,
    Address,
    LobAddressValidationResponse,
    PasswordChangeRequest,
    UserFeedbackEventAction
} from "@/shared/types";
import {
    getContactInformation,
    setContactInformation as userServiceSetContactInformation,
    verifyEmailChange,
    lobAddressVerification,
    saveUserEmailUpdate,
    updateUserIdpUsername,
    updateUserIdpPassword,
    updateUserProfile
} from "@/shared/api/userService";
import {
    verifyHsaUser,
    setContactInformation as bankServiceSetContactInformation
} from "@/shared/api/bankService";
import * as storeHelper from "@/shared/storeHelper";
import { fetchStateKeyMatch } from "@/shared/fetchStateHelper";
import { defineStore } from "pinia";
import _ from "lodash";
import { useWizardStore } from "@/employee/store/wizard";
import { useNotifierStore } from "@/shared/store/notifier";
import { useUserFeedbackStore } from "@/employee/store/userFeedback";
import { useCustodianSettingsStore } from "@/shared/store/custodianSettings";

const initialLobState: LobAddressValidationResponse = {
    result: LobAddressValidationResult.None,
    validationFootnotes: null,
    isValid: false,
    suggestedAddress: {
        addressLine1: "",
        addressLine2: "",
        city: "",
        state: "",
        zip: "",
    },
};

export const initialState: ContactInfoState = {
    contactInformationState: FetchState.Uninitialized,
    userConfirmedInfo: false,
    fetchStateKey: null,
    contactInformationValidationErrors: [],
    contactInformation: {
        firstName: "",
        middleInitial: "",
        lastName: "",
        email: "",
        phoneNumber: "",
        mobilePhoneNumber: "",
        isMailingSameAsPhysical: false,
        dateOfBirth: "",
        mailingAddress: {
            addressLine1: "",
            addressLine2: "",
            city: "",
            state: "",
            zip: "",
        },
        physicalAddress: {
            addressLine1: "",
            addressLine2: "",
            city: "",
            state: "",
            zip: "",
        },
        lastFourSsn: "",
        memberNumber: "",
        lastLogin: "",
        username: "",
        mothersMaidenName: ""
    },
    mailingAddressValidated: false,
    showLobAddressSuggestionModal: false,
    lobAddressSuggestion: { ...initialLobState },
};

export const useContactInfoStore = defineStore("contactInfo", {
    state: () => (_.cloneDeep(initialState)),
    getters: {
        validationErrors: state => state.contactInformationValidationErrors,
        isLoaded: state => state.contactInformationState === FetchState.Loaded,
        isLoading: state => state.contactInformationState === FetchState.Loading,
    },
    actions: {
        setContactInformation (
            payload: { fetchStateKey: FetchStateKey, contactInformation: ContactInformation }
        ) {
            if (this.contactInformationState !== FetchState.Loading ||
                !fetchStateKeyMatch(this.fetchStateKey, payload.fetchStateKey)) {
                return;
            }

            this.contactInformation = payload.contactInformation;
            this.contactInformationState = FetchState.Loaded;
        },
        setContactInformationLoading (
            payload: { fetchStateKey: FetchStateKey }
        ) {
            this.contactInformationState = FetchState.Loading;
            this.fetchStateKey = payload.fetchStateKey;
        },
        invalidateContactInformation (key: FetchStateKey): void {
            if (fetchStateKeyMatch(key, this.fetchStateKey)) {
                return;
            }

            this.contactInformation = initialState.contactInformation;
            this.contactInformationState = FetchState.Uninitialized;
            this.fetchStateKey = null;
        },
        setValidationErrors (validationErrors: string[]) {
            this.contactInformationValidationErrors = validationErrors;
        },
        userDidConfirmInfo () {
            this.userConfirmedInfo = true;
        },
        setLobValidationSuggestion (data: LobAddressValidationResponse) {
            this.lobAddressSuggestion = data;
        },
        resetLobValidationSuggestion () {
            this.lobAddressSuggestion = { ...initialLobState };
        },
        toggleLobSuggestionModal (value: boolean) {
            this.showLobAddressSuggestionModal = value;
        },
        setMailingAddressValidation (value: boolean) {
            this.mailingAddressValidated = value;
        },
        clearValidationErrors () {
            return this.setValidationErrors([]);
        },
        updateUserIdpUsername (username: string) {
            const notifierStore = useNotifierStore();
            const userFeedbackStore = useUserFeedbackStore();
            return updateUserIdpUsername(username)
                .then(() => {
                    notifierStore.setSuccess("Your username was successfully updated.");
                    userFeedbackStore.setUserFeedbackAction(UserFeedbackEventAction.MyProfile);
                })
                .catch(error => storeHelper.handleError({ error, rethrow: true }));
        },
        updateUserIdpPassword (request: PasswordChangeRequest) {
            const notifierStore = useNotifierStore();
            const userFeedbackStore = useUserFeedbackStore();
            return updateUserIdpPassword(request)
                .then(() => {
                    notifierStore.setSuccess("Your password was successfully updated.");
                    userFeedbackStore.setUserFeedbackAction(UserFeedbackEventAction.MyProfile);
                })
                .catch(error => storeHelper.handleError({ error, rethrow: true }));
        },
        updateUserEmail (
            args: { username: string, email: string, partnerId: number },
        ): Promise<any> {
            return saveUserEmailUpdate(args.username, args.email, args.partnerId).then(response => {
                return response;
            }).catch(err => storeHelper.handleError({ error: err, rethrow: true }));
        },
        getContactInformation (
            options?: { username?: string, partnerId?: number },
        ) {

            options = options || {};

            const fetchStateKey = {
                username: options.username,
                partnerId: options.partnerId
            };

            this.invalidateContactInformation(fetchStateKey);

            if (this.contactInformationState !== FetchState.Uninitialized) {
                Promise.resolve();
            }

            this.setContactInformationLoading({ fetchStateKey });

            return getContactInformation(options.username, options.partnerId)
                .then(response => response.data)
                .then(contactInformation => this.setContactInformation({ fetchStateKey, contactInformation }))
                .catch(err => storeHelper.handleError({ error: err, rethrow: true }));
        },
        saveContactInformation (request): Promise<any> {
            const validationError = "VALIDATION_ERROR";
            return verifyHsaUser(request)
                .then(response => {
                    this.setValidationErrors(response.data.validationMessages);
                    if (response.data.validationMessages.length > 0) {
                        return Promise.reject(validationError);
                    }
                })
                .then(response => {
                    const custodianSettingsStore = useCustodianSettingsStore();
                    const wizardStore = useWizardStore();
                    if (custodianSettingsStore.contactDetailsSetAtBank && wizardStore.isComplete) {
                        return bankServiceSetContactInformation(request);
                    }
                    return userServiceSetContactInformation(request);
                })
                .then(response => this.setContactInformation({ contactInformation: response.data }))
                .catch(err => {
                    storeHelper.handleError({ error: err, rethrow: true });
                    return Promise.reject();
                });
        },
        saveProfile (profile: ContactInformation) {
            return updateUserProfile(profile)
                .then(response => {
                    this.contactInformation = response.data;

                    const notifierStore = useNotifierStore();
                    notifierStore.setSuccess("You successfully updated your profile information.");

                    const userFeedbackStore = useUserFeedbackStore();
                    userFeedbackStore.setUserFeedbackAction(UserFeedbackEventAction.EditProfile);
                })
                .catch(error => {
                    return storeHelper.handleError({ error, rethrow: true });
                });
        },
        saveContactInformationClaimOnboarding (request): Promise<any> {
            return Promise.resolve(request).then(response => {
                    const custodianSettingsStore = useCustodianSettingsStore();
                    const wizardStore = useWizardStore();
                    if (custodianSettingsStore.contactDetailsSetAtBank && wizardStore.isComplete) {
                        return bankServiceSetContactInformation(request);
                    }
                    return userServiceSetContactInformation(request);
                })
                .then(response => this.setContactInformation({ contactInformation: response.data }))
                .catch(err => {
                    return storeHelper.handleError({ error: err, rethrow: true });
                });
        },
        verifyEmailChange (verificationCode: string): Promise<any> {
            return verifyEmailChange(verificationCode)
                .then(_ => {
                    const notifierStore = useNotifierStore();
                    notifierStore.setSuccess("Your email address has been updated. Use the new address to login.");
                    this.invalidateContactInformation();
                }).catch(err => {
                    storeHelper.handleError({ error: err, rethrow: false });
                    return;
                });
        },
        validateAddress (address: Address): Promise<any> {
            if ((this.lobAddressSuggestion.suggestedAddress === null ||
                    this.lobAddressSuggestion.suggestedAddress === undefined) ||
                this.lobAddressSuggestion.suggestedAddress.addressLine1 !== address.addressLine1 ||
                this.lobAddressSuggestion.suggestedAddress.addressLine2 !== address.addressLine2 ||
                this.lobAddressSuggestion.suggestedAddress.city !== address.city ||
                this.lobAddressSuggestion.suggestedAddress.state !== address.state) {
                this.resetLobValidationSuggestion();
                return lobAddressVerification({ address, properCase: false }).then(response => {
                    this.setLobValidationSuggestion(response.data);
                    this.toggleLobSuggestionModal(response.data.result !== LobAddressValidationResult.Confirmed);
                    if (response.data.result !== LobAddressValidationResult.Confirmed) {
                        return Promise.reject(
                            { lobSuggestion: true });
                    }
                }).catch(err => {
                    if (err && err.lobSuggestion) {
                        return Promise.reject();
                    }
                    storeHelper.handleError({ error: err, rethrow: true });
                });
            } else {
                this.toggleLobSuggestionModal(true);
                return Promise.reject();
            }
        }
    }
});
